/** @jsxImportSource @emotion/react */

import { Box, Skeleton, Typography, useTheme } from '@mui/joy';
import { useDataFormPage } from '../../providers/DataFormPageProvider';
import { usePageContent } from '../../providers/PageContentProvider';
import { Page } from './Page';
import { SideNav } from '../Nav/SideNav';
import {
  Chart,
  ChartWContent,
  Content,
  DataAppendix,
  ElectionCycle,
  MethodologySection,
  OtherPages,
  PageHeading,
  PageSubTitle,
  PageTitle,
  ScrollSection,
  Section
} from '../Display';
import { ChapterPreviewContainer } from '../Chapters/ChapterPreviewContainer';
import { DatatableProps } from '../Datatable/Datatable';
import { getChapters } from '../../lib/const';
import { ReturnToHomepageButton } from '../Nav/ReturnToHomepageButton';
import { useYear } from '../../providers/YearProvider';
import { Link } from '../../types';
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { EdgeCaseProvider } from '../../providers/EdgeCaseProvider';
import { LinkWChart, LinkWChartTopic } from '../../types/link';
import { DataKey } from '../../lib/model';
import ChartSectionProvider from '../../providers/ChartSectionProvider';
import ProportionSection from '../Charts/ProportionSection';
import TotalRaisedSection from '../Charts/TotalRaisedSection';
import { NavButton } from '../Nav/NavButton';
import { css } from '@emotion/react';
import { useMediaQuery } from '../../hooks';
import DonorSectionProvider from '../../providers/DonorSectionProvider';
import DonorSection from '../Charts/DonorSection';

interface DataFormPageProps<T extends Record<string, string | number>>
  extends Partial<DatatableProps<T>>,
    PropsWithChildren {
  chapter: 'state' | 'national' | 'donors';
  chapterHighlightViz: React.ReactNode;
}

const styles = {
  ul: css`
    list-style-position: inside;
    padding-left: 0;
  `,
  li: css`
    margin-bottom: 24px;
  `
};

const donorStyles = {
  ul: css`
    list-style-position: inside;
    padding-left: 0;
  `,
  li: css`
    margin-bottom: 42px;
    display: flex;
    gap: 16px;
    align-items: center;
    white-space: wrap;
  `
};

export const DataFormPage = <T extends Record<string, string | number>>({
  chapter,
  chapterHighlightViz,
  headCells,
  idKey,
  children
}: DataFormPageProps<T>) => {
  const { isFetching, apiData, state } = useDataFormPage();
  const [data, setData] = useState(apiData);
  const [isLoading, setIsLoading] = useState(isFetching);
  const { page } = useDataFormPage();

  const pageContent = usePageContent();

  const { year } = useYear();
  const CHAPTERS = getChapters(year);

  const theme = useTheme();

  // Helper to get content with provider-first fallback
  // Provider values should always be used; API data serves as safety fallback
  const getContent = (
    providerValue: string | undefined,
    apiValue: string | undefined
  ): string => {
    return providerValue || apiValue || '';
  };

  // Helper to get chart heading based on index
  const getChartHeading = (index: number, apiValue: string | undefined): string => {
    const sectionKey = `section_${index + 1}`;
    return getContent(pageContent.sectionHeadings[sectionKey], apiValue);
  };

  const altChapter = () => {
    if (chapter === 'state') {
      return 'candidates';
    }
    if (chapter === 'national') {
      return 'opportunities';
    }
    return chapter;
  };

  useEffect(() => {
    if (!isFetching && apiData?.data && apiData.data.length > 0) {
      setTimeout(() => {
        setData(apiData);
        setIsLoading(isFetching);
      }, 500);
    }
    if (isFetching) {
      //setIsLoading(isFetching);
    }
  }, [isFetching, apiData]);

  const getName = (index: number | undefined) => {
    const matches = CHAPTERS[chapter].links.filter(
      (l) => (l as LinkWChart).keyStatIndex === index
    );
    if (matches.length === 1) {
      return (matches[0] as LinkWChart).href;
    }
    return 'unknown';
  };

  const getChart = (index: number | undefined) => {
    const matches = CHAPTERS[chapter].links.filter(
      (l) => (l as LinkWChart).keyStatIndex === index
    );
    if (matches.length === 1) {
      return (matches[0] as LinkWChart).chart;
    }
    return 'unknown';
  };

  const getTopic = (index: number | undefined) => {
    const matches = CHAPTERS[chapter].links.filter(
      (l) => (l as LinkWChartTopic).keyStatIndex === index
    );
    if (matches.length === 1) {
      return (matches[0] as LinkWChartTopic).topic;
    }
    return 'unknown';
  };

  const highlightsRef = useRef<HTMLDivElement | null>(null);

  const navRef = useRef<HTMLDivElement | null>(null);
  const matches = useMediaQuery(theme.breakpoints.up('smallLaptop'));

  const regex = /<li>(\d+%)((?:<strong>.*?<\/strong>)?.*?)<\/li>/g;

  useEffect(() => {
    // Calculate the offset from the top
    const offsetTop = matches ? 89 : 206;

    const handleScroll = () => {
      const scrollY = window.scrollY;

      if (!navRef.current) {
        return;
      }

      if (scrollY >= offsetTop) {
        // Once scrolled past the offset, make the div fixed;
        navRef.current.style.top = matches ? '192px' : '43px';
      } else {
        // If above the offset, make the div static
        navRef.current.style.top = `${(matches ? 285 : 244) - scrollY}px`;
      }
    };

    // Attach the scroll event listener
    window.addEventListener('scroll', handleScroll);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [navRef, matches]);

  useEffect(() => {
    highlightsRef.current?.scrollTo(0, 0);
  }, [state]);

  if (!data) {
    return <div style={{ height: '100vh', backgroundColor: '#DADEDA' }}></div>;
  }
  return (
    <Page
      page={page as string}
      sx={{
        marginTop: '40px',
        padding: { xs: '5px', md: '15px' },
        marginX: 'auto',
        position: 'relative'
      }}
    >
      <EdgeCaseProvider>
        {children}
        <Box
          id='content'
          sx={{
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <ScrollSection name='highlights'>
            <Content>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between',
                  gap: '16px',
                  marginBottom: { xs: '2rem', md: 0 },
                  paddingRight: '1rem'
                }}
              >
                <PageHeading
                  bold={year.toString()}
                  regular='Women, Money, & Politics Watch'
                />
                <ReturnToHomepageButton />
              </Box>
              <PageTitle title={CHAPTERS[chapter].header} />
              <PageSubTitle subTitle={CHAPTERS[chapter].subheader} />
              <Section sx={{ marginTop: '15px' }}>
                <Typography level='body1'>
                  <Skeleton loading={isLoading}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: getContent(
                          pageContent.introText,
                          data?.data[0]?.field_intro_text?.processed
                        ).replace(
                          /href=(?![^>]*election=)/g,
                          'target="_blank" rel="noopener noreferrer" href='
                        )
                      }}
                    />
                  </Skeleton>
                </Typography>
                <Typography
                  level='annotation'
                  sx={{
                    fontSize: '0.875rem',
                    marginTop: '-5px',
                    marginBottom: '42px'
                  }}
                >
                  <Skeleton loading={isLoading}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: getContent(
                          pageContent.lastUpdated,
                          data?.data[0]?.field_last_updated?.processed
                        ).replace(
                          /href=/g,
                          'target="_blank" and rel="noopener noreferrer" href='
                        )
                      }}
                    />
                  </Skeleton>
                </Typography>
              </Section>
            </Content>

            <Chart>
              <ChapterPreviewContainer
                chapter={altChapter()}
                splatterBg
                backgroundGradientProps={{
                  gradientHeight: 400,
                  sx: {
                    paddingX: { xs: '15px', md: '44.5px' },
                    paddingY: '44.5px',
                    maxWidth: '1139px',
                    display: 'flex',
                    flexDirection: 'row'
                  }
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'column', lg: 'row' },
                    justifyContent: 'space-between',
                    gap: '60px',
                    padding: { xs: '10px', sm: '44.5px' },
                    boxShadow: '0px 1px 4px #00000029',
                    flexGrow: 1
                  }}
                >
                  <Box
                    sx={{
                      height: { xs: '100%', md: 540 }
                    }}
                  >
                    {chapterHighlightViz}

                    <Box sx={{ flexGrow: 1 }} />
                    {page != 'donorNational' && <ElectionCycle />}
                  </Box>
                  <Box
                    sx={{
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'flex-start',
                      alignContent: 'flex-start',
                      flexDirection: 'column',
                      maxHeight: { xs: '100%', md: 540 },
                      maxWidth: 512,
                      flexGrow: 0
                    }}
                  >
                    <Typography level='h2' sx={{ paddingBottom: '18px' }}>
                      {page &&
                        (page === 'national'
                          ? 'Congressional Highlights - Nationwide'
                          : 'Highlights')}
                    </Typography>
                    <Box
                      component={'div'}
                      ref={highlightsRef}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: {
                          xs: '100%',
                          md:
                            page === 'state' || page === 'donorState'
                              ? 308
                              : 460
                        },
                        overflow: 'auto'
                      }}
                    >
                      <Typography
                        level='body1'
                        sx={{ '& li:': { fontSize: '1.5rem' } }}
                      >
                        <Skeleton loading={isLoading}>
                          <div
                            // eslint-disable-next-line react/no-unknown-property
                            css={
                              page === 'donorNational' || page === 'donorState'
                                ? donorStyles
                                : styles
                            }
                            dangerouslySetInnerHTML={{
                              __html:
                                page === 'donorNational' ||
                                page === 'donorState'
                                  ? getContent(
                                      pageContent.highlights,
                                      data?.data[0]?.field_highlights?.processed
                                    )
                                      .replace(
                                        /href=/g,
                                        'target="_blank" and rel="noopener noreferrer" href='
                                      )
                                      .replace(
                                        regex,
                                        '<li><span style="font-size: 74px; font-weight: bold;">$1</span><span>$2</span></li>'
                                      )
                                  : getContent(
                                      pageContent.highlights,
                                      data?.data[0]?.field_highlights?.processed
                                    ).replace(
                                      /href=/g,
                                      'target="_blank" and rel="noopener noreferrer" href='
                                    )
                            }}
                          />
                        </Skeleton>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </ChapterPreviewContainer>
            </Chart>
            {(data?.data[0]?.field_annotation_primary !== null ||
              data?.data[0]?.field_annotation_secondary !== null) && (
              <Content>
                {data?.data[0]?.field_annotation_primary !== null && (
                  <Section
                    sx={{
                      marginTop: '-32px',
                      maxWidth: '936px',
                      width: '100%'
                    }}
                  >
                    <Typography level='annotation'>
                      <Skeleton loading={isLoading}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              data?.data[0]?.field_annotation_primary?.processed.replace(
                                /href=/g,
                                'target="_blank" and rel="noopener noreferrer" href='
                              ) ?? ''
                          }}
                        />
                      </Skeleton>
                    </Typography>
                  </Section>
                )}
                {data?.data[0]?.field_annotation_secondary !== null && (
                  <Section sx={{ marginTop: '70px' }}>
                    <Typography level='body1'>
                      <Skeleton loading={isLoading}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              data?.data[0]?.field_annotation_secondary?.processed.replace(
                                /href=/g,
                                'target="_blank" and rel="noopener noreferrer" href='
                              ) ?? ''
                          }}
                        />
                      </Skeleton>
                    </Typography>
                  </Section>
                )}
              </Content>
            )}
          </ScrollSection>

          {data?.data[0]?.field_key_stats.map((stat, index) => {
            const name = getName(index);
            const chart = getChart(index);
            const topic = getTopic(index);

            return (
              <ScrollSection key={`${stat.id}-${index}`} name={name}>
                <ChartWContent
                  chart={
                    <Box
                      sx={{
                        maxWidth: { xs: '100%', lg: 1050 },
                        width: '100%'
                      }}
                    >
                      {chart === 'TotalRaised' && (
                        <ChartSectionProvider
                          page={chapter}
                          topic={DataKey.TotalRaised}
                        >
                          <TotalRaisedSection
                            page={chapter}
                            text={
                              <Skeleton loading={isLoading}>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      stat.field_ks_chart_description?.processed.replace(
                                        /href=/g,
                                        'target="_blank" and rel="noopener noreferrer" href='
                                      ) ?? ''
                                  }}
                                />
                              </Skeleton>
                            }
                            title={
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: getChartHeading(
                                    index,
                                    stat.field_ks_chart_heading?.processed
                                  ).replace(
                                    /href=/g,
                                    'target="_blank" and rel="noopener noreferrer" href='
                                  )
                                }}
                              />
                            }
                          />
                        </ChartSectionProvider>
                      )}
                      {chart === 'SelfFinanced' && (
                        <ChartSectionProvider
                          page={chapter}
                          topic={DataKey.PropSelf}
                        >
                          <ProportionSection
                            page={chapter}
                            text={
                              <Skeleton loading={isLoading}>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      stat.field_ks_chart_description?.processed.replace(
                                        /href=/g,
                                        'target="_blank" and rel="noopener noreferrer" href='
                                      ) ?? ''
                                  }}
                                />
                              </Skeleton>
                            }
                            title={
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: getChartHeading(
                                    index,
                                    stat.field_ks_chart_heading?.processed
                                  ).replace(
                                    /href=/g,
                                    'target="_blank" and rel="noopener noreferrer" href='
                                  )
                                }}
                              />
                            }
                            topic='prop_self'
                          />
                        </ChartSectionProvider>
                      )}
                      {chart === 'SmallContributions' && (
                        <ChartSectionProvider
                          page={chapter}
                          topic={DataKey.PropSmall}
                        >
                          <ProportionSection
                            page={chapter}
                            text={
                              <Skeleton loading={isLoading}>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      stat.field_ks_chart_description?.processed.replace(
                                        /href=/g,
                                        'target="_blank" and rel="noopener noreferrer" href='
                                      ) ?? ''
                                  }}
                                />
                              </Skeleton>
                            }
                            title={
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: getChartHeading(
                                    index,
                                    stat.field_ks_chart_heading?.processed
                                  ).replace(
                                    /href=/g,
                                    'target="_blank" and rel="noopener noreferrer" href='
                                  )
                                }}
                              />
                            }
                            topic='prop_small'
                          />
                        </ChartSectionProvider>
                      )}
                      {chart &&
                        topic &&
                        chart !== 'unknown' &&
                        topic !== 'unknown' && (
                          <DonorSectionProvider
                            page={page ?? 'donorState'}
                            topic={topic}
                          >
                            <DonorSection
                              page={page ?? 'donorState'}
                              title={
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: getChartHeading(
                                      index,
                                      stat.field_ks_chart_heading?.processed
                                    ).replace(
                                      /href=/g,
                                      'target="_blank" and rel="noopener noreferrer" href='
                                    )
                                  }}
                                />
                              }
                              text={
                                <Skeleton loading={isLoading}>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        stat.field_ks_chart_description?.processed.replace(
                                          /href=/g,
                                          'target="_blank" and rel="noopener noreferrer" href='
                                        ) ?? ''
                                    }}
                                  />
                                </Skeleton>
                              }
                              topic={topic}
                            />
                          </DonorSectionProvider>
                        )}
                    </Box>
                  }
                  text={
                    <Skeleton loading={isLoading}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            stat.field_ks_text?.processed.replace(
                              /href=/g,
                              'target="_blank" and rel="noopener noreferrer" href='
                            ) ?? ''
                        }}
                      />
                    </Skeleton>
                  }
                />
                {index === data?.data?.[0]?.field_key_stats.length - 1 && (
                  <OtherPages currentChapter={altChapter()} />
                )}
              </ScrollSection>
            );
          })}

          {headCells && idKey && (
            <DataAppendix
              htmlText={getContent(
                pageContent.dataAppendix,
                data?.data[0]?.field_data_appendix?.processed
              ).replace(
                /href=/g,
                'target="_blank" and rel="noopener noreferrer" href='
              )}
              idKey={idKey}
              headCells={headCells}
              searchPlaceholder='Search by Candidate Name'
              searchable
            />
          )}
          {data && (
            <MethodologySection
              htmlText={getContent(
                pageContent.methodology,
                data?.data[0]?.field_methodology?.processed
              ).replace(
                /href=/g,
                'target="_blank" and rel="noopener noreferrer" href='
              )}
            />
          )}
        </Box>

        <Box
          component={'div'}
          ref={navRef}
          style={{ top: matches ? '285px' : '244px', position: 'fixed' }}
          sx={{
            left: '8.5px',
            zIndex: 3,
            visibility: { xs: 'hidden', md: 'visible' }
          }}
        >
          {' '}
          {!isLoading && (
            <SideNav
              chapter={altChapter()}
              links={CHAPTERS[chapter].links.filter(
                (link: Link) =>
                  !link.dynamic ||
                  data?.data[0]?.field_key_stats
                    .map((_, index) => getName(index))
                    .includes(link.href)
              )}
            />
          )}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            // top: '-0.6rem',
            // left: { xs: '-2.5rem', sm: '-2.25rem' },
            top: { xs: '-2.5rem', sm: '-0.6rem' },
            left: { xs: '.4rem', sm: '-2.25rem' },
            visibility: { xs: 'visible', md: 'hidden' }
          }}
        >
          <NavButton />
        </Box>
      </EdgeCaseProvider>
    </Page>
  );
};
